import { useCallback, useEffect, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { formatAdminBusinessEt } from '@/lib/admin-datetime';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { dynamicFormsApi } from '@/lib/api/dynamic-forms';
import type { RescheduleFeeWaiver } from '@/types/dynamic-form';
function isLikelyObjectId(s: string) {
  return /^[a-fA-F0-9]{24}$/.test(s.trim());
}

type Props = {
  formId: string;
  /** Appointment fields from the form draft (ids + labels). */
  appointmentFields: { id: string; label: string }[];
};

export function RescheduleFeeWaiversPanel({ formId, appointmentFields }: Props) {
  const [items, setItems] = useState<RescheduleFeeWaiver[]>([]);
  const [loading, setLoading] = useState(true);
  const [userIdFilter, setUserIdFilter] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const [newUserId, setNewUserId] = useState('');
  const [newAppointmentFieldId, setNewAppointmentFieldId] = useState(
    () => appointmentFields[0]?.id ?? ''
  );
  const [newReason, setNewReason] = useState('');
  const [newExpiresAt, setNewExpiresAt] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await dynamicFormsApi.listRescheduleFeeWaivers(formId, {
        userId: userIdFilter.trim() || undefined,
      });
      if (res.statusCode !== 200) {
        throw new Error(res.message || 'Failed to load waivers');
      }
      setItems(res.data.items ?? []);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to load waivers');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [formId, userIdFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (createOpen && appointmentFields.length && !appointmentFields.some((a) => a.id === newAppointmentFieldId)) {
      setNewAppointmentFieldId(appointmentFields[0].id);
    }
  }, [createOpen, appointmentFields, newAppointmentFieldId]);

  const resetCreateForm = () => {
    setNewUserId('');
    setNewAppointmentFieldId(appointmentFields[0]?.id ?? '');
    setNewReason('');
    setNewExpiresAt('');
  };

  const handleCreate = async () => {
    const uid = newUserId.trim();
    if (!isLikelyObjectId(uid)) {
      toast.error('Student id must be a 24-character Mongo ObjectId.');
      return;
    }
    if (!newAppointmentFieldId) {
      toast.error('Select an appointment field.');
      return;
    }
    const reason = newReason.trim();
    if (!reason) {
      toast.error('Reason is required.');
      return;
    }
    if (reason.length > 2000) {
      toast.error('Reason must be at most 2000 characters.');
      return;
    }
    let expiresAt: string | null | undefined = null;
    if (newExpiresAt.trim()) {
      const d = new Date(newExpiresAt);
      if (Number.isNaN(d.getTime())) {
        toast.error('Invalid expiry date.');
        return;
      }
      expiresAt = d.toISOString();
    }

    setCreating(true);
    try {
      const res = await dynamicFormsApi.createRescheduleFeeWaiver(formId, {
        appointmentFieldId: newAppointmentFieldId,
        userId: uid,
        reason,
        expiresAt,
      });
      if (res.statusCode !== 201) {
        throw new Error(res.message || 'Failed to create waiver');
      }
      toast.success('Waiver created');
      setCreateOpen(false);
      resetCreateForm();
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to create waiver');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (waiverId: string) => {
    setRevokingId(waiverId);
    try {
      const res = await dynamicFormsApi.revokeRescheduleFeeWaiver(formId, waiverId);
      if (res.statusCode !== 200) {
        throw new Error(res.message || 'Failed to revoke');
      }
      toast.success('Waiver revoked');
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to revoke');
    } finally {
      setRevokingId(null);
    }
  };

  const appointmentLabel = (id: string) => appointmentFields.find((a) => a.id === id)?.label ?? id;

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between">
        <div className="grid max-w-sm gap-1">
          <Label htmlFor="waiver-user-filter">Filter by student id</Label>
          <div className="flex gap-2">
            <Input
              id="waiver-user-filter"
              className="font-mono text-xs"
              placeholder="Optional Mongo ObjectId"
              value={userIdFilter}
              onChange={(e) => setUserIdFilter(e.target.value)}
            />
            <Button type="button" variant="secondary" onClick={() => void load()}>
              Apply
            </Button>
          </div>
        </div>
        <Button
          type="button"
          className="gap-2"
          disabled={appointmentFields.length === 0}
          onClick={() => {
            resetCreateForm();
            setCreateOpen(true);
          }}
        >
          <Plus className="h-4 w-4" />
          Grant waiver
        </Button>
      </div>

      {appointmentFields.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Add at least one appointment field to the form draft to grant reschedule fee waivers.
        </p>
      ) : null}

      <div className="rounded-xl border border-border/60 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-[28%]">Student</TableHead>
              <TableHead className="w-[18%]">Appointment field</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead className="w-[12%]">Status</TableHead>
              <TableHead className="w-[12%] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} className="h-24 text-center text-muted-foreground">
                  <Loader2 className="inline h-5 w-5 animate-spin" aria-hidden />
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="h-20 text-center text-sm text-muted-foreground">
                  No waivers yet.
                </TableCell>
              </TableRow>
            ) : (
              items.map((w) => {
                const consumed = Boolean(w.consumedAt);
                const expired =
                  w.expiresAt && new Date(w.expiresAt).getTime() < Date.now() && !consumed;
                return (
                  <TableRow key={w._id}>
                    <TableCell className="font-mono text-xs align-top">{w.userId}</TableCell>
                    <TableCell className="text-sm align-top">{appointmentLabel(w.appointmentFieldId)}</TableCell>
                    <TableCell className="max-w-md align-top">
                      <p className="line-clamp-3 text-sm text-muted-foreground">{w.reason}</p>
                    </TableCell>
                    <TableCell className="align-top text-xs">
                      {consumed ? (
                        <span className="text-muted-foreground">Used</span>
                      ) : expired ? (
                        <span className="text-amber-600 dark:text-amber-400">Expired</span>
                      ) : (
                        <span className="text-emerald-600 dark:text-emerald-400">Active</span>
                      )}
                      {w.expiresAt ? (
                        <p className="mt-1 text-[10px] text-muted-foreground">
                          Expires {formatAdminBusinessEt(w.expiresAt)}
                        </p>
                      ) : null}
                      {w.consumedAt ? (
                        <p className="mt-1 text-[10px] text-muted-foreground">
                          {formatDistanceToNow(new Date(w.consumedAt), { addSuffix: true })}
                        </p>
                      ) : null}
                    </TableCell>
                    <TableCell className="text-right align-top">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="gap-1"
                        disabled={consumed || revokingId === w._id}
                        onClick={() => void handleRevoke(w._id)}
                      >
                        {revokingId === w._id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Trash2 className="h-3.5 w-3.5" />
                        )}
                        Revoke
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Grant reschedule fee waiver</DialogTitle>
            <DialogDescription>
              Covers the next qualifying reschedule for this student on the selected appointment field. Single use after a successful reschedule.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div className="grid gap-1">
              <Label htmlFor="waiver-user">Student id (Mongo ObjectId)</Label>
              <Input
                id="waiver-user"
                className="font-mono text-xs"
                value={newUserId}
                onChange={(e) => setNewUserId(e.target.value)}
                placeholder="24 hex characters"
              />
            </div>
            <div className="grid gap-1">
              <Label>Appointment field</Label>
              <Select value={newAppointmentFieldId || '__none'} onValueChange={(v) => setNewAppointmentFieldId(v === '__none' ? '' : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select field" />
                </SelectTrigger>
                <SelectContent>
                  {appointmentFields.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1">
              <Label htmlFor="waiver-reason">Reason</Label>
              <Textarea
                id="waiver-reason"
                rows={3}
                value={newReason}
                onChange={(e) => setNewReason(e.target.value)}
                maxLength={2000}
                placeholder="e.g. Campus closed — approved by director"
              />
              <p className="text-[11px] text-muted-foreground">{newReason.length}/2000</p>
            </div>
            <div className="grid gap-1">
              <Label htmlFor="waiver-expires">Expires (optional)</Label>
              <Input
                id="waiver-expires"
                type="datetime-local"
                value={newExpiresAt}
                onChange={(e) => setNewExpiresAt(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button type="button" disabled={creating} onClick={() => void handleCreate()}>
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create waiver'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
